import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSEO } from "../components/SEO";
import { JsonLd } from "../components/JsonLd";
import { serviceSchema } from "../lib/schema";
import { PageHero, Reveal, SectionTag } from "../components/Reveal";
import { BookCallButton } from "../components/CTA";
import { CtaBand } from "../components/CtaBand";
import { IMAGES } from "../config/images";

const TOPICS = [
  { title: "Seasonality", text: "Hajj demand moves in defined windows. Campaigns are planned around the season, not after it." },
  { title: "High-Intent Enquiries", text: "Budgets focused on prospects actively comparing Hajj packages from India." },
  { title: "Family Decision-Making", text: "Pilgrimage decisions often involve the whole family. Messaging respects that process." },
  { title: "Package Comparison", text: "Customers compare inclusions, hotels and pricing. Clear, structured communication wins attention." },
  { title: "Trust", text: "For a journey this significant, credibility is the conversion strategy." },
  { title: "Customer Education", text: "Content that answers real questions shortens the path from attention to enquiry." },
  { title: "Lead Follow-Up", text: "Structured follow-up so high-value enquiries never go cold in an inbox or chat list." },
  { title: "WhatsApp Communication", text: "Meet customers in the channel they already use every day." },
  { title: "Retargeting", text: "Stay present while families deliberate, compare and prepare to commit." },
];

export default function HajjMarketing() {
  useSEO(
    "Hajj Marketing Agency in India | Nexus Catalyst",
    "Hajj marketing for Indian travel agencies: seasonal campaign strategy, high-intent enquiries, WhatsApp follow-up and retargeting built for a high-consideration journey."
  );
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <main data-testid="hajj-marketing-page">
      <JsonLd
        data={serviceSchema({
          name: "Hajj Marketing for Indian Travel Agencies",
          description: "Seasonal Hajj campaign strategy, high-intent lead generation, WhatsApp follow-up and retargeting for Indian travel agencies.",
          path: "/hajj-marketing",
        })}
      />
      <PageHero
        tag="Hajj Marketing"
        testid="hajj-hero"
        crumbs={[{ label: "Hajj Marketing", to: "/hajj-marketing" }]}
        lines={[
          "Hajj marketing for",
          <>Indian <span className="text-tan">travel agencies.</span></>,
        ]}
        copy="Hajj is a high-consideration travel decision where trust, timing, clarity and communication matter. Your digital marketing needs to reflect that."
      >
        <BookCallButton label="Book a Hajj Growth Strategy Call" testid="hajj-hero-cta" />
      </PageHero>

      <section className="bg-white py-24 md:py-32" data-testid="hajj-topics">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-3xl">
            <SectionTag className="mb-6">What Shapes the Work</SectionTag>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
              Hajj Demand Is Seasonal. Your System Shouldn't Be.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TOPICS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05} className="h-full">
                <div
                  className="h-full bg-cream border border-charcoal/10 p-7 transition-colors duration-300 hover:border-gold/50"
                  data-testid={`hajj-topic-${i + 1}`}
                >
                  <span className="text-xs font-semibold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold tracking-tight text-charcoal">{t.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/60 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section ref={imgRef} className="relative h-[380px] md:h-[480px] overflow-hidden" data-testid="hajj-image-band">
        <motion.img
          src={IMAGES.architectureTall}
          alt="Mosque minaret rising into a clear sky"
          style={{ y }}
          className="absolute inset-0 w-full h-full object-cover scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-charcoal/55" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex items-center">
          <Reveal>
            <p className="text-2xl md:text-4xl font-bold tracking-tight text-cream max-w-2xl leading-snug">
              When the season arrives, the agencies with a system are already
              fully booked with conversations.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Plan Your Hajj Season Before the Market Does."
        copy="Tell us about your packages, your cities and your capacity. We'll map the acquisition strategy around them."
        testid="hajj-cta-band"
      />
    </main>
  );
}
